import React, {FC, useCallback} from 'react';

import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {ReplyFactModel} from '../../../../../libs/shared/core/src/models/api/factModel';
import {MessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {ConversationLingeringRuleModel} from '../../../../models/api/conversationLingeringRuleModel';
import {MacrosBaseModel} from '../../../../models/api/macroModel';
import {fetchMacrosAsyncAction} from '../../../../store/data/macros/macroActions';
import {AiReplyButton} from './aiReplyButton';
import {LingeringRuleButton} from './lingeringRuleButton';
import {MacroButton} from './macroButton';
import {PickMacroDropdown} from './pickMacroDropdown';

export interface MacroTriggersProps {
  message: MessageModel;
  activityId: number;
  aiReply?: ReplyFactModel;
  lingeringRule?: ConversationLingeringRuleModel;
  macros: ReadonlyArray<MacrosBaseModel>;
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId;
}

export const MacroTriggers: FC<MacroTriggersProps> = ({
  message,
  activityId,
  aiReply,
  lingeringRule,
  macros,
  panelId,
}) => {
  const [firstMacro] = macros;

  const dispatch = useAppDispatch();

  // Lazy-load macros.
  useInitialEffect(() => {
    dispatch(fetchMacrosAsyncAction());
  });

  const renderDropdown = useCallback(
    (popoverProps: PopoverTransferProps) => (
      <PickMacroDropdown
        {...popoverProps}
        macros={macros}
        conversationId={message.conversationId}
        activityId={activityId}
        computeIsMacroRecommended={() => false} // When we know how to recommend macros, we can use this.
      />
    ),
    [activityId, macros, message.conversationId],
  );

  const shouldRenderDropdown = (aiReply && macros.length > 0) || macros.length > 1;
  const maybeRenderDropdown = shouldRenderDropdown ? renderDropdown : undefined;

  if (lingeringRule && lingeringRule.isInteractive) {
    return <LingeringRuleButton lingeringRule={lingeringRule} />;
  }

  if (aiReply) {
    return (
      <AiReplyButton
        aiReply={aiReply}
        message={message}
        renderDropdown={maybeRenderDropdown}
        panelId={panelId}
      />
    );
  }

  if (firstMacro) {
    return (
      <MacroButton
        macro={firstMacro}
        conversationId={message.conversationId}
        activityId={activityId}
        renderDropdown={maybeRenderDropdown}
      />
    );
  }

  return null;
};
